/**
 * Card-badge derivation for the required-documents-checklist `cards` layout (#20323).
 *
 * The Figma "Documents v1.3" design layers an expiry dimension on top of the
 * existing review state: an approved document can be "Geldig", "Verloopt
 * binnenkort" (within `thresholdDays`) or "Verlopen" depending on its
 * einddatum. Review states that are not approved map directly onto their own
 * badge. Kept as pure, framework-free functions so the mapping is
 * unit-testable without Angular (same pattern as `.roles.ts`).
 */

import type { UploadFileTone } from 'app/shared/components/upload/upload.types';

/** Review state as derived by the checklist component (`rowState`). */
export type DocumentRowState = 'approved' | 'pending' | 'ready' | 'rejected' | 'expired' | 'missing';

export type DocumentBadge = 'valid' | 'expiring' | 'expired' | 'rejected' | 'pending' | 'ready' | 'missing';

export type DocumentReviewState = Exclude<DocumentRowState, 'expired'>;
export type DocumentValidityState = 'none' | 'valid' | 'expiring' | 'expired';

export interface DocumentPresentation {
  reviewState: DocumentReviewState;
  validityState: DocumentValidityState;
  daysUntilExpiry: number | null;
  expiresAt: Date | null;
  blocksOnboarding: boolean;
}

export interface DocumentCardBadge {
  badge: DocumentBadge;
  /** Whole days until the einddatum (negative = expired that many days ago); null without a parseable einddatum. */
  daysUntilExpiry: number | null;
  /** Parsed einddatum, for display formatting. */
  expiresAt: Date | null;
}

/** Design decision (#20323, user-approved): "verloopt binnenkort" threshold. */
export const EXPIRING_SOON_DAYS = 30;

const MS_PER_DAY = 86_400_000;

function parseDate(raw: unknown): Date | null {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * Keeps review and validity independent. A near-expiry warning never changes
 * an approved review into a pending workflow state. Persisted `expired`
 * records are a compatibility shape for a previously approved document: their
 * validity is re-derived from the current einddatum, so extending the einddatum
 * back to a valid date recovers the document to "Geldig" instead of leaving it
 * permanently "Verlopen". Only when there is no parseable einddatum do we keep
 * the persisted `expired` validity as a fallback.
 */
export function deriveDocumentPresentation(input: {
  state: DocumentRowState;
  endDate: unknown;
  hasFile?: boolean;
  metadataComplete?: boolean;
  now?: Date;
  thresholdDays?: number;
}): DocumentPresentation {
  const now = input.now ?? new Date();
  const threshold = input.thresholdDays ?? EXPIRING_SOON_DAYS;
  const expiresAt = parseDate(input.endDate);
  const daysUntilExpiry = expiresAt ? Math.round((startOfDay(expiresAt) - startOfDay(now)) / MS_PER_DAY) : null;
  const explicitExpired = input.state === 'expired';
  const reviewState: DocumentReviewState = input.state === 'expired' ? 'approved' : input.state;

  let validityState: DocumentValidityState = 'none';
  if (reviewState === 'approved') {
    if (daysUntilExpiry !== null && daysUntilExpiry < 0) {
      validityState = 'expired';
    } else if (daysUntilExpiry !== null && daysUntilExpiry <= threshold) {
      validityState = 'expiring';
    } else if (daysUntilExpiry !== null) {
      validityState = 'valid';
    } else if (explicitExpired) {
      // Persisted 'expired' zonder parseerbare einddatum → behoud 'expired'.
      validityState = 'expired';
    }
  }

  const blocksOnboarding =
    reviewState !== 'approved' ||
    input.hasFile === false ||
    input.metadataComplete === false ||
    validityState === 'expired';

  return { reviewState, validityState, daysUntilExpiry, expiresAt, blocksOnboarding };
}

export function documentReviewLabel(reviewState: DocumentReviewState): string {
  switch (reviewState) {
    case 'approved':
      return 'Goedgekeurd';
    case 'rejected':
      return 'Afgekeurd';
    case 'pending':
      return 'In behandeling';
    case 'ready':
      return 'Aangeleverd';
    default:
      return 'Te uploaden';
  }
}

export function documentValidityLabel(presentation: DocumentPresentation): string | null {
  switch (presentation.validityState) {
    case 'valid':
      return 'Geldig';
    case 'expired':
      return 'Verlopen';
    case 'expiring':
      if (presentation.daysUntilExpiry === 0) return 'Verloopt vandaag';
      if (presentation.daysUntilExpiry === 1) return 'Verloopt morgen';
      return presentation.daysUntilExpiry == null
        ? 'Verloopt binnenkort'
        : `Verloopt over ${presentation.daysUntilExpiry} dagen`;
    default:
      return null;
  }
}

/**
 * Maps review state + einddatum onto the design's badge. Derived from the SAME
 * source as `deriveDocumentPresentation` (review + validity), so the badge can
 * never disagree with the labels a card shows: a date-recovered persisted
 * `expired` record with a future einddatum reads as "Goedgekeurd" + "Geldig"
 * AND gets a `valid` badge — not a green label sitting under a red "Verlopen"
 * marker in the Aandacht segment (#20323 review LOW).
 *
 * Only `approved` documents get expiry-driven badges — a rejected or in-review
 * document keeps its review badge even when its einddatum has passed (the
 * review verdict is the more actionable signal).
 */
export function deriveCardBadge(input: {
  state: DocumentRowState;
  endDate: unknown;
  now?: Date;
  thresholdDays?: number;
}): DocumentCardBadge {
  const { reviewState, validityState, daysUntilExpiry, expiresAt } = deriveDocumentPresentation(input);

  if (reviewState !== 'approved') {
    return { badge: reviewState, daysUntilExpiry, expiresAt };
  }
  switch (validityState) {
    case 'expired':
      return { badge: 'expired', daysUntilExpiry, expiresAt };
    case 'expiring':
      return { badge: 'expiring', daysUntilExpiry, expiresAt };
    default:
      return { badge: 'valid', daysUntilExpiry, expiresAt };
  }
}

/**
 * Entry values every document REPLACE writes: the review returns to pending
 * and the previous verdict is cleared. Shared by the checklist inline-replace
 * and the documents-cards runner (`fixedEntryValues`) so the two replace
 * paths cannot drift (#20723).
 */
export const DOCUMENT_REPLACE_RESET_FIELD_KEYS = <const>['status', 'review-comment', 'reviewed-at', 'reviewed-by'];

export function documentReplaceResetValues<T>(pendingStatus: T): {
  status: T;
  'review-comment': null;
  'reviewed-at': null;
  'reviewed-by': null;
} {
  return {
    status: pendingStatus,
    'review-comment': null,
    'reviewed-at': null,
    'reviewed-by': null,
  };
}

/** Lead-time (dateOffset value-object) for the expiry-notification schedule. */
export interface DocumentNotificationLeadTime {
  dateOffsetInMinutes: number;
  dateOffsetType: 'before' | 'after' | 'none';
  dateOffsetValue: number;
  dateOffsetUnit: string;
}

export const DOCUMENT_NOTIFICATION_DEFAULT_LEAD_TIME: Readonly<DocumentNotificationLeadTime> = Object.freeze({
  dateOffsetInMinutes: 20_160,
  dateOffsetType: 'before',
  dateOffsetValue: 14,
  dateOffsetUnit: 'days',
});

/**
 * Lead-time snapshot written on a document record at creation/replacement; the
 * server-side dateField action reads `entry.notification-lead-time` as its
 * dynamic offset against `entry.end-date` (#20724). Template requiring an end
 * date: its `notificatieTermijn` (or the 14-day default); template without
 * that requirement: null (clears the snapshot); free document: default.
 */
export function documentNotificationLeadTime(
  template: Record<string, unknown> | null | undefined
): DocumentNotificationLeadTime | null {
  if (!template) return { ...DOCUMENT_NOTIFICATION_DEFAULT_LEAD_TIME };
  const requiresEndDate = template['vereisEinddatum'] === true || template['vereisEinddatum'] === 'true';
  if (!requiresEndDate) return null;
  const term = template['notificatieTermijn'] as DocumentNotificationLeadTime | null | undefined;
  return term ? { ...term } : { ...DOCUMENT_NOTIFICATION_DEFAULT_LEAD_TIME };
}

/**
 * Combined status-badge presentation — the ONE badge per document row. Shared
 * by the documents-cards template and the required-documents checklist so both
 * surfaces render identical label/color/icon semantics for a given badge
 * (chips-unificatie #20660; kleuren via de --dd-badge-* tokens, nooit
 * hardcoded zodat dark mode blijft werken).
 */
export function documentBadgeLabel(badge: DocumentCardBadge): string {
  switch (badge.badge) {
    case 'valid':
      return 'Geldig';
    case 'expiring':
      if (badge.daysUntilExpiry === 0) return 'Verloopt vandaag';
      if (badge.daysUntilExpiry === 1) return 'Verloopt morgen';
      return badge.daysUntilExpiry == null ? 'Verloopt binnenkort' : `Verloopt over ${badge.daysUntilExpiry} dagen`;
    case 'expired':
      return badge.expiresAt ? `Verlopen ${formatLongDateNl(badge.expiresAt)}` : 'Verlopen';
    case 'rejected':
      return 'Afgekeurd';
    case 'pending':
      return 'In behandeling';
    case 'ready':
      return 'Aangeleverd';
    default:
      return 'Te uploaden';
  }
}

/**
 * Semantic colour role per badge. Split out of documentBadgeClasses so surfaces that need the
 * tone without the Tailwind class string (the uploaded-file row) share one mapping.
 */
export function documentBadgeTone(badge: DocumentCardBadge): UploadFileTone {
  switch (badge.badge) {
    case 'valid':
      return 'positive';
    case 'expiring':
    case 'pending':
    case 'ready':
      return 'warning';
    case 'expired':
    case 'rejected':
      return 'negative';
    default:
      return 'neutral';
  }
}

/**
 * Complete literal class strings, not string interpolation. Tailwind's content scanner reads
 * source files as text and only emits a utility for a class it can see written out verbatim; a
 * template literal built from a runtime tone value (e.g.
 * a template literal that splices the tone into the var() name) is invisible to the scanner, so the arbitrary-value
 * utility never reaches the compiled stylesheet even though the string looks correct in the DOM.
 * Keep this a full lookup table — do not "simplify" it back into interpolation.
 */
const TONE_BADGE_CLASSES: Record<UploadFileTone, string> = {
  positive: 'bg-[var(--dd-badge-positive-bg)] text-[var(--dd-badge-positive-fg)]',
  warning: 'bg-[var(--dd-badge-warning-bg)] text-[var(--dd-badge-warning-fg)]',
  negative: 'bg-[var(--dd-badge-negative-bg)] text-[var(--dd-badge-negative-fg)]',
  neutral: 'bg-[var(--dd-badge-neutral-bg)] text-[var(--dd-badge-neutral-fg)]',
};

export function documentBadgeClasses(badge: DocumentCardBadge): string {
  return TONE_BADGE_CLASSES[documentBadgeTone(badge)];
}

export function documentBadgeIcon(badge: DocumentCardBadge): string {
  switch (badge.badge) {
    case 'valid':
      return 'heroicons_solid:check-circle';
    case 'expired':
    case 'expiring':
    case 'rejected':
      return 'heroicons_solid:exclamation-triangle';
    case 'pending':
      return 'heroicons_solid:clock';
    case 'ready':
      return 'heroicons_solid:paper-airplane';
    default:
      return 'heroicons_solid:arrow-up-tray';
  }
}

/**
 * Sort weight per badge — lower sorts higher up the list (#20933).
 *
 * The order is "how urgently does the holder have to act", which is what the
 * Aandacht segment is for: an already-expired document is the hardest failure,
 * a near-expiry one is the deadline the user asked to see first, and a rejected
 * document needs the same re-upload as a missing one but carries a verdict the
 * user has not seen yet. `ready`/`pending` sit at the back of that segment
 * because the ball is with the reviewer, not the holder. `valid` is last so the
 * Alles segment groups the settled documents at the bottom instead of
 * interleaving them.
 */
export const DOCUMENT_BADGE_SORT_ORDER: Readonly<Record<DocumentBadge, number>> = Object.freeze({
  expired: 0,
  expiring: 1,
  rejected: 2,
  missing: 3,
  ready: 4,
  pending: 5,
  valid: 6,
});

/** The two things an ordering needs from a document row: its badge and its display title. */
export interface DocumentCardSortable {
  badge: DocumentCardBadge;
  title: string;
}

/** Titles are user-entered Dutch, so compare them the way a Dutch reader expects (é, ij, casing). */
const TITLE_COLLATOR = new Intl.Collator('nl', { sensitivity: 'base', numeric: true });

/**
 * Badge group first, then the nearest einddatum, then alphabetically.
 *
 * `daysUntilExpiry` is already signed relative to today, so ascending puts the
 * longest-expired document above a barely-expired one and the soonest-expiring
 * above a distant one — no separate "overdue" branch needed. A document without
 * a parseable einddatum has no deadline to rank on and sorts to the back of its
 * own group rather than jumping ahead of dated ones.
 */
export function compareDocumentCards(a: DocumentCardSortable, b: DocumentCardSortable): number {
  const rank = DOCUMENT_BADGE_SORT_ORDER[a.badge.badge] - DOCUMENT_BADGE_SORT_ORDER[b.badge.badge];
  if (rank !== 0) return rank;

  const aDays = a.badge.daysUntilExpiry;
  const bDays = b.badge.daysUntilExpiry;
  if (aDays !== bDays) {
    if (aDays === null) return 1;
    if (bDays === null) return -1;
    return aDays - bDays;
  }

  return TITLE_COLLATOR.compare(a.title, b.title);
}

/**
 * Order document rows without mutating the input.
 *
 * `project` is applied once per row (decorate–sort–undecorate) because deriving
 * a badge parses dates; doing it inside the comparator would repeat that work
 * on every comparison.
 */
export function sortDocumentCards<T>(items: readonly T[], project: (item: T) => DocumentCardSortable): T[] {
  return items
    .map(item => ({ item, key: project(item) }))
    .sort((a, b) => compareDocumentCards(a.key, b.key))
    .map(({ item }) => item);
}

function formatLongDateNl(raw: Date | string): string {
  const d = raw instanceof Date ? raw : new Date(raw);
  return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
}
